import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, BarChart3, BriefcaseBusiness, CheckCircle2, Database, FileSignature, LayoutDashboard, Plus, RefreshCw, Save, Settings2, SquareCheckBig, X } from 'lucide-react';
import BDDashboard from './BDDashboard';
import { dashboardIconOptions, getDashboardIcon } from '../utils/dashboardIcons';
import '../styles/ModularDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.casedb.co';

const fallbackBootstrap = {
    success: true,
    tables_installed: false,
    permissions: {
        salary_data: false,
        business_development: false,
        global_admin: false,
    },
    dashboards: [],
    options: {
        case_types: [],
        first_primaries: [],
        signature_templates: [],
    },
    widgets: [
        { widget_key: 'tasks_overview', name: 'Tasks Overview', category: 'productivity', permission_key: 'everyone', default_width: 4, default_height: 2, default_config: { scope: 'my_tasks' } },
        { widget_key: 'case_snapshot', name: 'Case Snapshot', category: 'cases', permission_key: 'everyone', default_width: 4, default_height: 2, default_config: { scope: 'my_cases', include_archived: false, case_type_id: null, first_primary_id: null } },
        { widget_key: 'case_type_breakdown', name: 'Case Type Breakdown', category: 'cases', permission_key: 'everyone', default_width: 6, default_height: 3, default_config: { scope: 'all_active', case_type_id: null, first_primary_id: null } },
        { widget_key: 'signature_tracker', name: 'Signature Tracker', category: 'signatures', permission_key: 'everyone', default_width: 4, default_height: 2, default_config: { scope: 'my_cases', case_type_id: null, first_primary_id: null } },
        { widget_key: 'template_signature_tracker', name: 'Template Signature Tracking', category: 'signatures', permission_key: 'everyone', default_width: 6, default_height: 2, default_config: { scope: 'my_cases', signature_template_id: null } },
        { widget_key: 'case_comms_summary', name: 'Case Comms Summary', category: 'cases', permission_key: 'everyone', default_width: 8, default_height: 4, default_config: { scope: 'my_cases', case_type_id: null, first_primary_id: null, result_limit: 5 } },
        { widget_key: 'attorney_performance', name: 'Attorney Performance', category: 'performance', permission_key: 'salary_data', default_width: 6, default_height: 3, default_config: { scope: 'my_data' } },
        { widget_key: 'bd_dashboard', name: 'Business Development', category: 'business_development', permission_key: 'business_development', default_width: 8, default_height: 4, default_config: { view: 'report' } },
    ],
    dashboard: {
        id: null,
        name: 'New Dashboard',
        icon_key: 'layout',
        source_template_key: null,
        widgets: [],
    },
};

const widgetIcons = {
    tasks_overview: SquareCheckBig,
    case_snapshot: BriefcaseBusiness,
    case_type_breakdown: BarChart3,
    signature_tracker: FileSignature,
    template_signature_tracker: FileSignature,
    case_comms_summary: BriefcaseBusiness,
    attorney_performance: Database,
    bd_dashboard: Database,
};

const metricLabels = {
    open_tasks: 'Open',
    overdue_tasks: 'Overdue',
    due_this_week: 'Due 7 Days',
    total_cases: 'Total',
    active_cases: 'Active',
    archived_cases: 'Archived',
    case_types: 'Types',
    open_requests: 'Open',
    completed_requests: 'Completed',
    total_requests: 'Total',
    pending_requests: 'Pending',
    in_progress_requests: 'In Progress',
    signed_requests: 'Signed',
    total_template_requests: 'Total',
    matched_cases: 'Matched',
    summaries_available: 'Summaries',
};

const scopeOptions = [
    { value: 'my_cases', label: 'My cases' },
    { value: 'my_team', label: 'My team' },
    { value: 'all_active', label: 'All active' },
    { value: 'all_including_archived', label: 'All + archived' },
];

const normalizeDashboard = (payload) => {
    const merged = {
        ...fallbackBootstrap,
        ...payload,
        permissions: { ...fallbackBootstrap.permissions, ...(payload?.permissions || {}) },
        widgets: payload?.widgets?.length ? payload.widgets : fallbackBootstrap.widgets,
        dashboards: payload?.dashboards || fallbackBootstrap.dashboards,
        options: {
            ...fallbackBootstrap.options,
            ...(payload?.options || {}),
        },
    };

    const catalogByKey = Object.fromEntries(merged.widgets.map((widget) => [widget.widget_key, widget]));
    const baseDashboard = payload?.dashboard ? { ...fallbackBootstrap.dashboard, ...payload.dashboard } : fallbackBootstrap.dashboard;

    return {
        ...merged,
        dashboard: {
            ...baseDashboard,
            widgets: (baseDashboard.widgets || []).map((widget, index) => ({
                id: widget.id || `${widget.widget_key}-${index}`,
                width: widget.width || catalogByKey[widget.widget_key]?.default_width || 4,
                height: widget.height || catalogByKey[widget.widget_key]?.default_height || 2,
                config: widget.config || catalogByKey[widget.widget_key]?.default_config || {},
                catalog: widget.catalog || catalogByKey[widget.widget_key],
                ...widget,
            })),
        },
    };
};

const createBlankDashboard = () => ({
    id: null,
    name: 'New Dashboard',
    icon_key: 'layout',
    source_template_key: null,
    widgets: [],
});

const formatMetricValue = (value) => Number(value || 0).toLocaleString();
const getWidgetIcon = (widgetKey) => widgetIcons[widgetKey] || LayoutDashboard;

const summarizeConfig = (config = {}) => {
    const parts = [];
    if (config.scope) {
        parts.push(scopeOptions.find((option) => option.value === config.scope)?.label || config.scope);
    }
    if (config.case_type_name) {
        parts.push(config.case_type_name);
    }
    if (config.first_primary_name) {
        parts.push(config.first_primary_name);
    }
    if (config.template_name) {
        parts.push(`Template: ${config.template_name}`);
    }
    if (config.result_limit) {
        parts.push(`Top ${config.result_limit}`);
    }
    if (config.include_archived) {
        parts.push('Archived included');
    }
    return parts.join(' · ');
};

const WidgetMetrics = ({ data }) => {
    const metrics = data?.metrics || {};
    const entries = Object.entries(metrics);

    if (!entries.length) {
        return <div className="modular-dashboard-empty">No metric data yet.</div>;
    }

    return (
        <div className="modular-dashboard-metrics">
            {entries.map(([key, value]) => (
                <div className="modular-dashboard-metric" key={key}>
                    <strong>{formatMetricValue(value)}</strong>
                    <span>{metricLabels[key] || key.replaceAll('_', ' ')}</span>
                </div>
            ))}
        </div>
    );
};

const WidgetList = ({ items }) => {
    if (!items?.length) {
        return null;
    }

    return (
        <div className="modular-dashboard-list">
            {items.slice(0, 6).map((item, index) => (
                <div className="modular-dashboard-list-row" key={`${item.id || item.label || index}`}>
                    <span>{item.title || item.label || item.status || 'Item'}</span>
                    <strong>{item.value ?? item.due_date ?? item.priority ?? ''}</strong>
                </div>
            ))}
        </div>
    );
};

const WidgetSummaryList = ({ items }) => {
    if (!items?.length) {
        return null;
    }

    return (
        <div className="modular-dashboard-summary-list">
            {items.map((item, index) => (
                <div className="modular-dashboard-summary-item" key={`${item.id || item.title || index}`}>
                    <div className="modular-dashboard-summary-header">
                        <strong>{item.title || 'Case'}</strong>
                        {item.updated_at ? <span>{new Date(item.updated_at).toLocaleDateString()}</span> : null}
                    </div>
                    {item.meta ? <div className="modular-dashboard-summary-meta">{item.meta}</div> : null}
                    <div className="modular-dashboard-summary-body">{item.summary || 'No summary available.'}</div>
                </div>
            ))}
        </div>
    );
};

const WidgetSettings = ({ widget, onChange, onClose }) => {
    const config = widget.config || {};
    const options = widget.options || {};
    const supportsScope = !['tasks_overview', 'attorney_performance', 'bd_dashboard'].includes(widget.widget_key);
    const supportsCaseFilters = ['case_snapshot', 'case_type_breakdown', 'signature_tracker', 'template_signature_tracker', 'case_comms_summary'].includes(widget.widget_key);

    const updateConfig = (updates) => {
        onChange({
            ...widget,
            config: {
                ...config,
                ...updates,
            },
        });
    };

    return (
        <div className="modular-dashboard-settings">
            <div className="modular-dashboard-settings-row">
                <label>Title</label>
                <input
                    value={widget.title || ''}
                    onChange={(event) => onChange({ ...widget, title: event.target.value })}
                    placeholder={widget.catalog?.name || 'Widget title'}
                />
            </div>
            {supportsScope && (
                <div className="modular-dashboard-settings-row">
                    <label>Scope</label>
                    <select value={config.scope || 'my_cases'} onChange={(event) => updateConfig({ scope: event.target.value })}>
                        {scopeOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
            )}
            {supportsCaseFilters && (
                <div className="modular-dashboard-settings-row">
                    <label>Case Type</label>
                    <select
                        value={config.case_type_id || ''}
                        onChange={(event) => {
                            const selected = (options.case_types || []).find((item) => String(item.id) === event.target.value);
                            updateConfig({
                                case_type_id: event.target.value ? Number(event.target.value) : null,
                                case_type_name: selected?.name || '',
                            });
                        }}
                    >
                        <option value="">All case types</option>
                        {(options.case_types || []).map((caseType) => (
                            <option key={caseType.id} value={caseType.id}>{caseType.name}</option>
                        ))}
                    </select>
                </div>
            )}
            {supportsCaseFilters && (
                <div className="modular-dashboard-settings-row">
                    <label>First Primary</label>
                    <select
                        value={config.first_primary_id || ''}
                        onChange={(event) => {
                            const selected = (options.first_primaries || []).find((item) => String(item.id) === event.target.value);
                            updateConfig({
                                first_primary_id: event.target.value ? Number(event.target.value) : null,
                                first_primary_name: selected?.name || '',
                            });
                        }}
                    >
                        <option value="">All first primaries</option>
                        {(options.first_primaries || []).map((primary) => (
                            <option key={primary.id} value={primary.id}>{primary.name}</option>
                        ))}
                    </select>
                </div>
            )}
            {widget.widget_key === 'template_signature_tracker' && (
                <div className="modular-dashboard-settings-row">
                    <label>Signature Template</label>
                    <select
                        value={config.signature_template_id || ''}
                        onChange={(event) => {
                            const selected = (options.signature_templates || []).find((item) => String(item.id) === event.target.value);
                            updateConfig({
                                signature_template_id: event.target.value ? Number(event.target.value) : null,
                                template_name: selected?.name || '',
                            });
                        }}
                    >
                        <option value="">All signature templates</option>
                        {(options.signature_templates || []).map((template) => (
                            <option key={template.id} value={template.id}>{template.name}</option>
                        ))}
                    </select>
                </div>
            )}
            {widget.widget_key === 'case_comms_summary' && (
                <div className="modular-dashboard-settings-row">
                    <label>Results To Show</label>
                    <input
                        type="number"
                        min="1"
                        max="12"
                        value={config.result_limit || 5}
                        onChange={(event) => updateConfig({ result_limit: Number(event.target.value || 5) })}
                    />
                </div>
            )}
            <div className="modular-dashboard-settings-actions">
                <button type="button" className="action-micro" onClick={onClose}>Done</button>
            </div>
        </div>
    );
};

const DashboardWidget = ({ widget, editMode, onChange, onRemove, onOpenLegacyView, user }) => {
    const [settingsOpen, setSettingsOpen] = useState(false);
    const Icon = getWidgetIcon(widget.widget_key);
    const data = widget.data || {};
    const title = widget.title || widget.catalog?.name || widget.widget_key;
    const configSummary = summarizeConfig(widget.config);

    if (widget.widget_key === 'bd_dashboard' && data?.locked !== true) {
        return (
            <div className="modular-dashboard-card modular-dashboard-card-wide">
                <div className="modular-dashboard-card-header">
                    <div>
                        <span className="modular-dashboard-icon"><Icon size={18} /></span>
                        <strong>{title}</strong>
                    </div>
                    {editMode && <button type="button" className="action-micro quaternary" onClick={() => onRemove(widget)}><X size={14} /></button>}
                </div>
                <BDDashboard user={user} hideHeader pageContainerClassName="modular-dashboard-bd" defaultViewType={widget.config?.view === 'summaries' ? 'summaries' : 'report'} />
            </div>
        );
    }

    return (
        <div className={`modular-dashboard-card modular-dashboard-span-${Math.min(Number(widget.width || 4), 8)}`}>
            <div className="modular-dashboard-card-header">
                <div>
                    <span className="modular-dashboard-icon"><Icon size={18} /></span>
                    <strong>{title}</strong>
                </div>
                {editMode && (
                    <div className="modular-dashboard-card-actions">
                        <button type="button" className="action-micro quaternary" onClick={() => setSettingsOpen((prev) => !prev)} title="Widget settings">
                            <Settings2 size={14} />
                        </button>
                        <button type="button" className="action-micro quaternary" onClick={() => onRemove(widget)} title="Remove widget">
                            <X size={14} />
                        </button>
                    </div>
                )}
            </div>
            {configSummary && <div className="modular-dashboard-config-summary">{configSummary}</div>}
            {settingsOpen && <WidgetSettings widget={widget} onChange={onChange} onClose={() => setSettingsOpen(false)} />}
            {data.locked ? (
                <div className="modular-dashboard-empty">{data.message || 'You do not have access to this widget.'}</div>
            ) : data.link === 'legacy_attorney_dashboard' || data.link === 'legacy_bd_dashboard' ? (
                <button type="button" className="modular-dashboard-link-card" onClick={() => onOpenLegacyView(data.link)}>
                    <ArrowUpRight size={16} />
                    {data.message}
                </button>
            ) : widget.widget_key === 'case_comms_summary' ? (
                <>
                    <WidgetMetrics data={data} />
                    <WidgetSummaryList items={data.items} />
                    {!data.items?.length && <div className="modular-dashboard-empty">{data.message || 'No matching summaries yet.'}</div>}
                </>
            ) : (
                <>
                    <WidgetMetrics data={data} />
                    <WidgetList items={data.items} />
                    {!data.items?.length && data.message && <div className="modular-dashboard-empty">{data.message}</div>}
                </>
            )}
        </div>
    );
};

const WidgetLibrary = ({ widgets, dashboardWidgets, onAdd }) => {
    const dashboardKeys = new Set(dashboardWidgets.map((widget) => widget.widget_key));

    return (
        <div className="modular-dashboard-library">
            {widgets.map((widget) => (
                <button
                    type="button"
                    key={widget.widget_key}
                    className="modular-dashboard-library-item"
                    onClick={() => onAdd(widget)}
                    disabled={dashboardKeys.has(widget.widget_key)}
                >
                    <Plus size={14} />
                    <span>{widget.name}</span>
                </button>
            ))}
        </div>
    );
};

const DashboardMetaEditor = ({ dashboard, onChange }) => {
    const PreviewIcon = getDashboardIcon(dashboard.icon_key);

    return (
        <div className="modular-dashboard-meta-editor">
            <div className="modular-dashboard-name-row">
                <label>Dashboard name</label>
                <div className="modular-dashboard-name-input-wrap">
                    <span className="modular-dashboard-icon-preview"><PreviewIcon size={18} /></span>
                    <input
                        value={dashboard.name || ''}
                        onChange={(event) => onChange({ ...dashboard, name: event.target.value })}
                        placeholder="Example: My Daily Work"
                    />
                </div>
            </div>
            <div className="modular-dashboard-name-row">
                <label>Choose an icon</label>
                <div className="modular-dashboard-icon-picker">
                    {dashboardIconOptions.map((option) => {
                        const Icon = option.Icon;
                        return (
                            <button
                                type="button"
                                key={option.key}
                                className={`modular-dashboard-icon-option ${dashboard.icon_key === option.key ? 'active' : ''}`}
                                onClick={() => onChange({ ...dashboard, icon_key: option.key })}
                                title={option.label}
                            >
                                <Icon size={16} />
                                <span>{option.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const ModularDashboard = ({ user, dashboardId, mode = 'view', onOpenLegacyView, onDashboardCreated, onDashboardUpdated }) => {
    const [bootstrap, setBootstrap] = useState(() => normalizeDashboard(fallbackBootstrap));
    const [dashboard, setDashboard] = useState(() => createBlankDashboard());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editMode, setEditMode] = useState(mode === 'create');
    const [error, setError] = useState('');

    const catalogByKey = useMemo(() => Object.fromEntries((bootstrap.widgets || []).map((widget) => [widget.widget_key, widget])), [bootstrap.widgets]);
    const isCreateMode = mode === 'create' || !dashboard.id;

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const url = new URL(`${API_BASE_URL}/modular_dashboard.php`);
            url.searchParams.set('action', 'bootstrap');
            if (mode !== 'create' && dashboardId) {
                url.searchParams.set('dashboard_id', dashboardId);
            }

            const response = await fetch(url.toString(), { credentials: 'include' });
            if (!response.ok) {
                throw new Error(`Dashboard API unavailable (${response.status})`);
            }

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || 'Dashboard API failed');
            }

            const normalized = normalizeDashboard(data);
            setBootstrap(normalized);
            setDashboard(mode === 'create' ? createBlankDashboard() : normalized.dashboard);
            setEditMode(mode === 'create');
        } catch (apiError) {
            console.warn('Using local modular dashboard fallback:', apiError);
            const normalized = normalizeDashboard(fallbackBootstrap);
            setBootstrap(normalized);
            setDashboard(mode === 'create' ? createBlankDashboard() : normalized.dashboard);
            setEditMode(mode === 'create');
            setError('Local preview mode: API tables/endpoints are not available yet.');
        } finally {
            setLoading(false);
        }
    }, [dashboardId, mode]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const saveLayout = async () => {
        if (!bootstrap.tables_installed) {
            setError('Local preview only: run api/add_modular_dashboard_tables.sql before saving layouts.');
            return;
        }

        setSaving(true);
        try {
            const action = dashboard.id ? 'save_layout' : 'create_dashboard';
            const response = await fetch(`${API_BASE_URL}/modular_dashboard.php`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action,
                    dashboard_id: dashboard.id || null,
                    name: dashboard.name || 'New Dashboard',
                    icon_key: dashboard.icon_key || 'layout',
                    widgets: dashboard.widgets,
                }),
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Dashboard save failed');
            }

            const normalized = normalizeDashboard({ ...bootstrap, ...data });
            setBootstrap(normalized);
            setDashboard(normalized.dashboard);
            setEditMode(false);
            setError('');

            if (action === 'create_dashboard') {
                onDashboardCreated?.(normalized.dashboard, normalized.dashboards || []);
            } else {
                onDashboardUpdated?.(normalized.dashboard, normalized.dashboards || []);
            }
        } catch (saveError) {
            setError(saveError.message || 'Dashboard save failed.');
        } finally {
            setSaving(false);
        }
    };

    const addWidget = (widget) => {
        const nextIndex = dashboard.widgets.length;
        setDashboard((prev) => ({
            ...prev,
            widgets: [
                ...prev.widgets,
                {
                    id: `local-${widget.widget_key}-${Date.now()}`,
                    widget_key: widget.widget_key,
                    title: widget.name,
                    position_x: (nextIndex % 3) * 4,
                    position_y: Math.floor(nextIndex / 3) * 2,
                    width: widget.default_width || 4,
                    height: widget.default_height || 2,
                    config: widget.default_config || {},
                    catalog: widget,
                    data: {},
                },
            ],
        }));
    };

    const updateWidget = (updatedWidget) => {
        setDashboard((prev) => ({
            ...prev,
            widgets: prev.widgets.map((widget) => (widget.id === updatedWidget.id ? updatedWidget : widget)),
        }));
    };

    const removeWidget = (removedWidget) => {
        setDashboard((prev) => ({
            ...prev,
            widgets: prev.widgets.filter((widget) => widget.id !== removedWidget.id),
        }));
    };

    const hydratedWidgets = (dashboard.widgets || []).map((widget) => ({
        ...widget,
        catalog: widget.catalog || catalogByKey[widget.widget_key],
        options: bootstrap.options || fallbackBootstrap.options,
    }));

    return (
        <div className="modular-dashboard">
            <div className="modular-dashboard-hero">
                <div>
                    <div className="modular-dashboard-eyebrow"><LayoutDashboard size={16} /> {isCreateMode ? 'Create homepage dashboard' : 'Homepage dashboard'}</div>
                    <h3>{dashboard.name || 'New Dashboard'}</h3>
                    <p>{isCreateMode ? 'Choose an icon, add widgets, and save to place this dashboard directly in the homepage switcher.' : 'Edit widget scopes and layout. This dashboard stays available from the homepage switcher with its selected icon.'}</p>
                </div>
                <div className="modular-dashboard-actions">
                    <button type="button" className="action quaternary" onClick={loadDashboard} disabled={loading || saving}>
                        <RefreshCw size={14} /> Refresh
                    </button>
                    <button type="button" className="action quaternary" onClick={() => setEditMode((prev) => !prev)}>
                        <Settings2 size={14} /> {editMode ? 'Preview' : 'Customize'}
                    </button>
                    <button type="button" className="action alt" onClick={saveLayout} disabled={saving || loading}>
                        <Save size={14} /> {saving ? 'Saving...' : dashboard.id ? 'Save Dashboard' : 'Create Dashboard'}
                    </button>
                </div>
            </div>

            {error && <div className="modular-dashboard-notice">{error}</div>}
            {!bootstrap.tables_installed && (
                <div className="modular-dashboard-notice neutral">
                    <CheckCircle2 size={16} /> Local-safe mode is active. Saving turns on after running the modular dashboard SQL migration.
                </div>
            )}

            {(editMode || isCreateMode) && <DashboardMetaEditor dashboard={dashboard} onChange={setDashboard} />}

            {editMode && <WidgetLibrary widgets={bootstrap.widgets || []} dashboardWidgets={hydratedWidgets} onAdd={addWidget} />}

            {loading ? (
                <div className="modular-dashboard-empty large">Loading modular dashboard...</div>
            ) : hydratedWidgets.length ? (
                <div className="modular-dashboard-grid">
                    {hydratedWidgets.map((widget) => (
                        <DashboardWidget
                            key={widget.id || `${widget.widget_key}-${widget.position_x}-${widget.position_y}`}
                            widget={widget}
                            editMode={editMode}
                            onChange={updateWidget}
                            onRemove={removeWidget}
                            onOpenLegacyView={onOpenLegacyView}
                            user={user}
                        />
                    ))}
                </div>
            ) : (
                <div className="modular-dashboard-empty large modular-dashboard-empty-card">
                    Add widgets in customize mode to build this dashboard.
                </div>
            )}
        </div>
    );
};

export default ModularDashboard;
